import { pipe, Option, Record as R } from 'effect';

import type { ErpConnections } from '@/domain/erpIntegration/schemas';

export type CompanyErpConnection = { erp: string; status: string; companyName: string | undefined };

// The backend keeps one connection per company and answers every other ERP as 'disconnected'.
export const companyErpConnection = (connections: ErpConnections): CompanyErpConnection | null =>
  pipe(
    R.findFirst(connections, ({ status }) => status !== 'disconnected'),
    Option.map(([erp, { status, details }]) => ({ erp, status, companyName: details.company_name })),
    Option.getOrNull,
  );

// 'suspended' is still connected: only the background sync is paused.
export const isConnectedStatus = (status: string): boolean => status === 'connected' || status === 'suspended';

export const companyConnectedErp = (connections: ErpConnections): string | null =>
  pipe(
    Option.fromNullable(companyErpConnection(connections)),
    Option.filter(({ status }) => isConnectedStatus(status)),
    Option.map(({ erp }) => erp),
    Option.getOrNull,
  );

export const companyConnectedErpCompanyName = (connections: ErpConnections): string | null =>
  pipe(
    Option.fromNullable(companyErpConnection(connections)),
    Option.filter(({ status }) => isConnectedStatus(status)),
    Option.flatMap(({ companyName }) => Option.fromNullable(companyName)),
    Option.getOrNull,
  );

export const isCompanyErpConnected = (connections: ErpConnections): boolean =>
  R.some(connections, ({ status }) => isConnectedStatus(status));

export const isCompanyErpConnectRequired = (connections: ErpConnections): boolean =>
  !R.some(connections, ({ status }) => isConnectedStatus(status) || status === 'connecting');
